/**
 * Standardized empty state component
 * Provides consistent empty/no-data UI across the application
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import InboxIcon from '@mui/icons-material/Inbox';
import { logger } from '../../utils/logger';

const EmptyState = ({
  title = 'No data found',
  message = '',
  icon: CustomIcon,
  iconSize = 80,
  action,
  actionText = 'Add New',
  actionIcon = <AddIcon />,
  variant = 'default', // 'default', 'search', 'error'
  component = 'EmptyState',
  fullHeight = false,
  elevation = 0
}) => {
  // Select appropriate icon based on variant
  const getIcon = () => {
    if (CustomIcon) return CustomIcon;

    switch (variant) {
      case 'search':
        return SearchOffIcon;
      case 'error':
        return SearchOffIcon;
      default:
        return InboxIcon;
    }
  };

  const Icon = getIcon();

  React.useEffect(() => {
    logger.info(component, 'Empty state displayed', {
      title,
      variant,
      hasAction: !!action
    });
  }, [component, title, variant, action]);

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullHeight ? '400px' : '200px',
        p: 4,
        textAlign: 'center'
      }}
    >
      <Icon
        sx={{
          fontSize: iconSize,
          color: 'text.secondary',
          opacity: 0.5,
          mb: 2
        }}
      />
      <Typography
        variant="h6"
        color="text.secondary"
        gutterBottom
      >
        {title}
      </Typography>
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: action ? 3 : 0, maxWidth: 400 }}
        >
          {message}
        </Typography>
      )}
      {action && (
        <Button
          variant="contained"
          startIcon={actionIcon}
          onClick={() => {
            logger.info(component, 'Empty state action clicked', { actionText });
            action();
          }}
          sx={{ mt: 2 }}
        >
          {actionText}
        </Button>
      )}
    </Box>
  );

  if (elevation > 0) {
    return (
      <Paper elevation={elevation} sx={{ overflow: 'hidden' }}>
        {content}
      </Paper>
    );
  }

  return content;
};

export default EmptyState;
